import { ReactElement, useEffect } from 'react'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import {
  updateConnectClient,
  fetchConnectClients,
  selectConnectClientById,
} from '../../admin.slice'

interface EditConnectClientFormProps {
  isOpen: boolean
  clientID: string
  onClose: () => void
  isSaving: (bool: boolean) => void
  onSuccess?: () => void
  onError?: (error: string) => void
}

export default function EditConnectClientForm({
  isOpen,
  clientID,
  onClose,
  isSaving,
  onSuccess,
  onError,
}: EditConnectClientFormProps): ReactElement {
  let dispatch = useAppDispatch()
  let client = useAppSelector(selectConnectClientById(clientID))

  let validationSchema = z.object({
    clientName: z.string().nonempty('Client Name is Required'),
    clientID: z.string().nonempty('Client ID is Required'),
  })

  let form = useForm({
    defaultValues: {
      clientName: '',
      clientID: '',
    },
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      if (!clientID) return

      onClose()
      isSaving(true)
      try {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(
          updateConnectClient({
            id: clientID,
            data: {
              clientName: value.clientName,
              clientID: value.clientID,
            },
          }),
        ).unwrap()
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchConnectClients())
        form.reset()
        if (onSuccess) {
          onSuccess()
        }
      } catch (err: any) {
        if (onError) {
          onError(
            (err?.response?.data || err?.message) ??
              'Error Updating Connect Client',
          )
        }
        form.reset()
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchConnectClients())
      } finally {
        isSaving(false)
      }
    },
  })

  // Update form values when client data is loaded
  useEffect(() => {
    if (client) {
      form.setFieldValue('clientName', client.clientName)
      form.setFieldValue('clientID', client.clientID)
    }
  }, [client])

  let handleOnClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleOnClose}>
      <Paper sx={{ minWidth: { xs: 300, sm: 500 }, p: 3 }}>
        <DialogTitle sx={{ textAlign: 'center', p: 0, mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Edit Connect Client
          </Typography>
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent sx={{ p: 0, mb: 3 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
            >
              <form.Field
                name="clientName"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="Client Name"
                    placeholder="Customer Organization Name"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta)
                        ? getZodValidationError(field.state.meta.errors)
                        : 'The name of the customer or organization'
                    }
                  />
                )}
              />
              <form.Field
                name="clientID"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="Client ID"
                    placeholder="auth0_client_id_here"
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta)
                        ? getZodValidationError(field.state.meta.errors)
                        : 'The Auth0 M2M application client ID (found in azp JWT claim)'
                    }
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: 3,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isSubmitting || !state.isValid}
              children={(isDisabled) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={isDisabled}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
